/**
 * Two-leg match ("andata e ritorno") — a direct challenge played over two games,
 * the colours swapped between them. The return leg starts automatically after a
 * short countdown; a player who does not show up loses it by walkover.
 *
 * It can be asked for with ANY opening, but it does not mean the same thing on
 * both sides of that line, and the rating follows the difference:
 *
 *  - **Drawn opening.** The draw hands one side a structurally better position
 *    that the other player never gets to try, so the two legs replay the SAME
 *    opening and neither is rated on its own: leg 1 is parked and both games are
 *    rated together in the return leg's transaction. Either both count or neither
 *    does — only the return hands the favoured side to the other player. This is
 *    the default here.
 *  - **Free opening.** Nothing is being compensated: the two games are ordinary
 *    games with the colours swapped, and each is rated on its own, exactly as a
 *    single game would be. A played game must not vanish from the rating because
 *    the return never started. The default here is a single game — the return is
 *    offered, not assumed.
 *
 * The challenger chooses (`ChallengeSettings.legs`); {@link defaultLegsFor} is
 * what the choice starts from.
 */

import { ballotCeiling, isDrawnOpening, openingPlies, type OpeningMode } from './game-mode.js';
import { forzaLevel, type BallotOpening } from '../generated/openings.gen.js';

/** Games in a match played andata + ritorno. */
export const MATCH_LEGS_TWO = 2;

/** Games a challenge is played over: 1 = solo andata, 2 = andata e ritorno. */
export type MatchLegs = 1 | 2;

/** Selectable leg counts, ordered as shown in the challenge dropdown. */
export const MATCH_LEGS_OPTIONS: readonly MatchLegs[] = [2, 1] as const;

/**
 * Where the leg count starts from, before anyone chooses.
 *
 * The return leg can be asked for with any opening, but it is only PROPOSED
 * where it compensates a draw: with a free opening a challenge stays what it has
 * always been — one game — unless the challenger says otherwise.
 */
export function defaultLegsFor(opening: OpeningMode): MatchLegs {
  return isDrawnOpening(opening) ? MATCH_LEGS_TWO : 1;
}

/**
 * Openings whose draw can hand one side an advantage too big for a single game to
 * be fair: the ritorno is what compensates it, so without it the result must not
 * touch Glicko-2.
 *
 * Follows the F.I.D. rule (Regolamento Tecnico 2.2/2.3): tables B and C — the ≤2
 * strength ceilings — are explicitly usable "in incontri di una sola partita",
 * while the stronger tables are reserved for two-game matches. The random draws
 * are not capped at all: they can land on a 4N/4B position, so they are treated
 * like the strongest ballot.
 *
 * `random1` is the exception: a single random half-move cannot hand anyone an
 * advantage, because the opponent then replies freely from a position no draw
 * shaped. From two plies up the draw can already deliver a forced-capture line,
 * so the ritorno becomes load-bearing again.
 */
export function requiresTwoLegsForRating(
  opening: OpeningMode,
  scelta?: BallotOpening | null,
): boolean {
  /*
   * `single` answers from the opening ITSELF, not from a ceiling.
   *
   * The other eight are answered by the widest thing they could draw, because
   * that is all that is known before the draw happens. Here the opening is known:
   * asking "which table did it come from?" would refuse the rating to a PARI
   * opening that happens to be listed in the general table, and grant it to a 3N
   * that is not. The rule the federation wrote is about how lopsided the position
   * is, and with the row in hand that can be read directly.
   *
   * No row — an id that is missing, or one the table does not have — answers
   * TRUE: the prudent side of a question about the rating is the one that leaves
   * Glicko-2 alone.
   */
  if (opening === 'single') return scelta ? forzaLevel(scelta.forza) > 2 : true;
  const ceiling = ballotCeiling(opening);
  if (ceiling !== null) return ceiling > 2;
  return openingPlies(opening) >= 2;
}

/**
 * The combinations that can never be rated: an unbalanced drawn opening played as
 * a single game.
 *
 * Enforced server-side in `normalizeChallengeSettings` and again on the room in
 * `startChallengeGame`; the challenge modal hides the rated/casual selector for it.
 */
export function isUnratableSingleLeg(
  opening: OpeningMode,
  legs: MatchLegs,
  scelta?: BallotOpening | null,
): boolean {
  return requiresTwoLegsForRating(opening, scelta) && legs === 1;
}

/** How long the end-of-leg-1 countdown runs before the return leg auto-starts.
 *  Arenas and tournaments: there the event clock is running for everyone.
 *
 *  ⚠️ Non puo' scendere a zero: l'attesa e' PORTANTE, non cortesia. Vedi il
 *  commento di `armaRitorno` in `arena.handler.ts` — la stanza del ritorno nasce
 *  dopo che `handleGameEnd` ha ripulito `activeGamesMap`, e crearla subito
 *  farebbe scattare la guardia `inPartita()`. */
export const RETURN_LEG_COUNTDOWN_MS = 5000;

/**
 * Lo stesso conto alla rovescia, nelle SFIDE dirette.
 *
 * Due costanti e non una, anche adesso che il numero coincide: quella di sopra
 * e' vincolata dal cablaggio, questa e' solo quanto si fa aspettare il
 * giocatore, e le due sono libere di divergere di nuovo.
 *
 * Qui non c'e' l'orologio di un torneo che corre per tutti, e «Pronto» premuto
 * da entrambi fa partire il ritorno subito.
 */
export const CHALLENGE_RETURN_LEG_COUNTDOWN_MS = 5_000;

/** `result_reason` of a return leg lost because the player did not show up. */
export const REASON_WALKOVER = 'walkover';

/**
 * `result_reason` di una partita chiusa perche' la prima mossa non e' arrivata
 * entro la scadenza (`scadenzaPrimaMossaMs`).
 *
 * Lo stesso motivo serve i due esiti possibili, che non sono la stessa cosa:
 * fuori dalle competizioni la partita si ANNULLA (nessun rating, nessuna riga in
 * `games`), in arena si PERDE a tavolino. A distinguerli e' il percorso —
 * `handleGameVoid` contro `handleGameEnd` — non il motivo.
 */
export const REASON_NO_FIRST_MOVE = 'no_first_move';

/**
 * Match points keyed by userId: vedi `MATCH_SCORING`. Keyed by user (not by
 * colour) precisely because the colours swap between legs — a colour-keyed score
 * would be meaningless across a match.
 */
export type MatchScore = Record<string, number>;

/**
 * Quanto vale una manche nel punteggio di un incontro a due manche: **2 / 1 / 0**.
 *
 * La stessa scala dei tornei a turni (`SWISS_SCORING` in `./tournament.js`) e
 * delle arene (`ARENA_SCORING`), e non per caso: un incontro a due manche si
 * gioca dentro uno svizzero, dentro un'arena e come sfida diretta, e finche'
 * questo punteggio stava su 1 / 0,5 / 0 il conto dell'incontro scriveva
 * «½ - ½» a dodici pixel da una classifica a punti interi.
 *
 * Costante e non due letterali dentro `creditMatchScore` perche' e' un
 * contratto di filo: viaggia in `game:end` e in `match:*`, e il client la
 * formatta senza ricalcolarla.
 *
 * NON e' un alias di `SWISS_SCORING`: un incontro fra due giocatori e una
 * classifica di torneo sono due cose, e legarle vorrebbe dire che cambiare il
 * punteggio dei tornei cambia in silenzio anche le sfide dirette fuori torneo.
 * Che oggi coincidano e' una decisione, non una derivazione.
 */
export const MATCH_SCORING = {
  WIN: 2,
  DRAW: 1,
  LOSS: 0,
} as const;

/** Match progress attached to game:start / game:end / match:* payloads. */
export interface MatchInfo {
  /** 1 = andata, 2 = ritorno. */
  legIndex: number;
  totalLegs: number;
  /** True once the last leg has been played. */
  complete: boolean;
  score: MatchScore;
}
